import React, { ChangeEvent, ForwardedRef, forwardRef, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cropper } from '@components/drag-and-drop/src/cropper';
import { Props } from '@components/drag-and-drop/src/drag-and-drop.type';
import { getImagesList, isLargePng } from '@components/drag-and-drop/src/utils';
import { IconCross, IconImage, IconPencil } from '@components/icons';
import { OptimizedImage } from '@components/optimized-image';
import { FILE_TYPE_PNG } from '@constants/global';
import { useToast } from '@hooks/use-toast/use-toast';
import { getAcceptedFormatForInputFile } from '@utils/get-accepted-format-for-imput-file';
import { isFileFormatCorrect } from '@utils/is-file-format-correct';
import classNames from 'classnames';

// @see https://levelup.gitconnected.com/crop-images-on-upload-in-your-react-app-with-react-image-crop-5f3cd0ad2b35
// @see https://codesandbox.io/s/y09komm059?file=/src/canvasUtils.js
export const DragAndDrop = forwardRef(
  (
    {
      translations,
      allowedTypes,
      onChange,
      value,
      disableRemoveImage,
      disableCropperModal = false,
      removeLoadImageButton,
      imageWidth,
      imageFormat,
      breakpoints = {},
      disabled,
      aspectRatio,
      aspectRatioWrapperClassName = 'aspect-w-16 aspect-h-9',
      borderRadiusClassName = 'rounded-t-md rounded-bl-md rounded-br-3xl',
      hideHelper = false,
      backgroundButtonChangeImageColorClassName = 'bg-tertiary',
      absolutePositionButtonChangeImageColorClassName = 'absolute bottom-3 right-3',
    }: Props,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: ForwardedRef<HTMLImageElement>
  ) => {
    const { t } = useTranslation();
    const toast = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [src, setSrc] = useState(value ? value : null);
    const [tempSrc, setTempSrc] = useState<string | null>(null);
    const [tempFile, setTempFile] = useState<File | null>(null);
    const inputFile = useRef<HTMLInputElement>(null);

    const acceptedFormat = useMemo(
      () => getAcceptedFormatForInputFile({ acceptedFormat: allowedTypes }),
      [allowedTypes]
    );

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const MAX_FILE_SIZE = 1024 * 1024 * 10;
      const files = e.target.files;

      if (!files?.[0]) return;

      if (!isFileFormatCorrect({ type: files[0].type, allowedTypes })) {
        e.preventDefault();
        toast.push({
          title: t('errors:fileTypeIncorrectTitle'),
          text: t('errors:fileTypeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }
      if (files[0].size > MAX_FILE_SIZE) {
        e.preventDefault();
        toast.push({
          title: t('errors:fileSizeIncorrectTitle'),
          text: t('errors:fileSizeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }

      const imagesList = getImagesList(files);
      if (!imagesList.length) {
        return;
      }
      const { file: imageFile, src: imageSrc } = imagesList[0];
      const isBiggerThatAccepted = await isLargePng(files[0].type, imagesList[0]);
      if (isBiggerThatAccepted) {
        toast.push({
          title: t('errors:pngFileSizeIncorrectTitle'),
          text: t('errors:pngFileSizeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }
      if (hideHelper) {
        setTempFile(file);
        setTempSrc(src);
      }
      setFile(imageFile);
      setSrc(imageSrc);

      if (disableCropperModal) {
        onChange([imagesList[0]]);
      } else setIsOpen(true);
    };
    const handleCropCancel = () => {
      if (hideHelper) {
        setFile(tempFile);
        setSrc(tempSrc);
      } else {
        setFile(null);
        setSrc(null);
      }
      setIsOpen(false);
    };

    const handleCropSave = (image: { file: File; src: string }) => {
      const { file: imageFile, src: imageSrc } = image;
      setFile(imageFile);
      setSrc(imageSrc);
      onChange([image]);
      setIsOpen(false);
    };

    const handleChangeImage = () => {
      if (disabled) return;
      if (disableRemoveImage && inputFile.current) inputFile.current.click();
      else {
        setFile(null);
        setSrc(null);
        onChange([]);
      }
    };

    const image = { src: src ? src : '', file };

    const calculateFormat = () => {
      if (imageFormat && imageWidth) return imageFormat;
      if (!imageFormat && imageWidth) return FILE_TYPE_PNG;
      return undefined;
    };

    return (
      <div>
        <Cropper
          open={isOpen}
          setOpen={setIsOpen}
          image={image}
          onCancelCrop={handleCropCancel}
          onSaveCrop={handleCropSave}
          translations={translations}
          aspectRatio={aspectRatio}
        />

        <div className='relative'>
          {!!src && (
            <>
              <div
                className={classNames({
                  'flex justify-center flex-col': disableCropperModal,
                  [aspectRatioWrapperClassName]: !disableCropperModal,
                })}>
                <OptimizedImage
                  className={classNames(`object-contain ${borderRadiusClassName}`, {
                    'max-h-[38rem]': disableCropperModal,
                    'w-full': !disableCropperModal,
                  })}
                  src={src}
                  alt='Cover event'
                  width={imageWidth}
                  format={calculateFormat()}
                  fluid
                  loading='eager'
                  breakpoints={breakpoints}
                />
              </div>
              {!disabled && (
                <div className={`absolute ${absolutePositionButtonChangeImageColorClassName}`}>
                  <input onChange={onFileChange} accept={acceptedFormat} ref={inputFile} type='file' hidden />
                  {!removeLoadImageButton && (
                    <button
                      data-testid='drag-and-drop--button-change-image'
                      type='button'
                      className={`inline-flex items-center p-2 ${backgroundButtonChangeImageColorClassName} shadow-[-1px_2px_16px_0_rgba(0,0,0,0.15)] border border-soft rounded-md`}
                      onClick={handleChangeImage}>
                      {disableRemoveImage && <IconPencil className='h-5 w-5 text-primary' />}
                      {!disableRemoveImage && <IconCross className='h-5 w-5 text-primary' />}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {!src && (
            <div className='rounded-md w-full h-36 lg:h-96 fill-tertiary'>
              <label
                htmlFor='drag-and-drop-image-input'
                className='relative w-full h-full flex flex-col items-center justify-center'>
                <input
                  id='drag-and-drop-image-input'
                  aria-label='drag and drop image'
                  aria-hidden='true'
                  className='absolute left-0 top-0 opacity-0 w-full h-full outline-none cursor-pointer'
                  type='file'
                  data-testid='drag-and-drop-image-input'
                  name='drag-and-drop-image-input'
                  onChange={onFileChange}
                  accept={acceptedFormat}
                />

                <div>
                  <IconImage className='h-9 w-auto text-subtitle-m text-primary my-0 mx-auto' />
                  <p className='text-center mt-5 text-subtitle-s text-primary'>{translations.title}</p>
                  <p className='text-center text-body-xs text-secondary'>{translations.subtitle}</p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  }
);
